import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Heart, Shield, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Attestation = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    attestHealthcare: false,
    attestShortage: false,
    userType: 'healthcare_provider'
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.attestHealthcare || !formData.attestShortage) {
      toast.error('You must agree to both attestations to continue');
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API}/auth/attest`,
        {
          attested_healthcare_provider: formData.attestHealthcare,
          attested_shortage_area: formData.attestShortage,
          user_type: formData.userType
        },
        { withCredentials: true }
      );
      
      toast.success('Attestation recorded successfully!');
      navigate('/profile-setup');
    } catch (error) {
      console.error('Attestation error:', error);
      toast.error('Failed to submit attestation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f0f7ff] to-white flex items-center justify-center px-6">
      <div className="max-w-2xl w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#2563eb] to-[#1e40af] flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Healthcare Professional Attestation</h1>
          <p className="text-gray-600">
            Please confirm your status to continue
          </p>
        </div>

        {/* Attestation Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8">
          <div className="space-y-6">
            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-[#2563eb] flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">About Attestation</h3>
                  <p className="text-sm text-gray-700">
                    By attesting, you confirm that the information you provide is true and accurate. 
                    False attestations may result in account termination.
                  </p>
                </div>
              </div>
            </div>

            {/* Healthcare Provider Attestation */}
            <div className="space-y-4">
              <div className="flex items-start gap-3 p-4 border-2 border-gray-200 rounded-lg hover:border-[#2563eb] transition-colors">
                <Checkbox
                  id="attest-healthcare"
                  checked={formData.attestHealthcare}
                  onCheckedChange={(checked) => setFormData({ ...formData, attestHealthcare: checked })}
                  data-testid="attest-healthcare-checkbox"
                  className="mt-1"
                />
                <Label htmlFor="attest-healthcare" className="flex-1 cursor-pointer">
                  <div className="font-semibold text-gray-900 mb-1">
                    I am a licensed healthcare professional
                  </div>
                  <p className="text-sm text-gray-600">
                    I attest that I am a licensed healthcare provider (physician, nurse, nurse practitioner, 
                    physician assistant, or other licensed healthcare professional) actively practicing medicine.
                  </p>
                </Label>
              </div>

              <div className="flex items-start gap-3 p-4 border-2 border-gray-200 rounded-lg hover:border-[#2563eb] transition-colors">
                <Checkbox
                  id="attest-shortage"
                  checked={formData.attestShortage}
                  onCheckedChange={(checked) => setFormData({ ...formData, attestShortage: checked })}
                  data-testid="attest-shortage-checkbox"
                  className="mt-1"
                />
                <Label htmlFor="attest-shortage" className="flex-1 cursor-pointer">
                  <div className="font-semibold text-gray-900 mb-1">
                    I work in a healthcare shortage area
                  </div>
                  <p className="text-sm text-gray-600">
                    I attest that I am currently working or planning to work in a designated healthcare 
                    shortage area (HPSA) or underserved community.
                  </p>
                </Label>
              </div>
            </div>

            {/* Legal Notice */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-gray-700">
                <strong>Notice:</strong> This platform is designed for healthcare professionals serving 
                underserved communities. By submitting this attestation, you agree that you meet these 
                criteria and understand that false information may result in account suspension or legal action.
              </p>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={loading || !formData.attestHealthcare || !formData.attestShortage}
              data-testid="submit-attestation-button"
              className="w-full bg-[#2563eb] hover:bg-[#1e40af] text-white py-4 rounded-xl text-lg font-semibold disabled:opacity-50"
            >
              {loading ? 'Submitting...' : (
                <span className="flex items-center justify-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  Submit Attestation
                </span>
              )}
            </Button>
          </div>
        </form>

        {/* Footer */}
        <p className="text-center text-sm text-gray-500 mt-6">
          By continuing, you agree to our Terms of Service and Privacy Policy
        </p>
      </div>
    </div>
  );
};

export default Attestation;
